/**
 * executiveApi.js — API functions for the Executive Report page.
 *
 * fetchExecutiveReport       — runs the executive report query (yesterday)
 * fetchExecutiveFilterOptions — loads all filter option datasets
 * saveExecThreshold          — persists excessive response threshold to campus config
 */

import { apiSave, postReport } from '../../../../services/api.js';
import { reportFilterFields, responseTimeField } from '../../../../utils/reportRequestBody.js';


// ---------------------------------------------------------------------------
// Fetch Executive Report
// ---------------------------------------------------------------------------

/**
 * @param {object} opts
 * @param {number} opts.campusId
 * @param {string} opts.username
 * @param {string} opts.startDate — YYYY-MM-DD
 * @param {string} opts.endDate   — YYYY-MM-DD
 * @param {object} opts.filters
 * @param {number} opts.execTime  — excessive response threshold in minutes
 * @returns {Promise<object>}
 */
export async function fetchExecutiveReport({ campusId, username, startDate, endDate, filters = {}, execTime = 20, isCloud = false }) {
    const body = {
        rep_type: 'executiveReport',
        /* Selects which event proc the report Lambda runs: reason 0 is a real
           service period on a cloud campus and ambiguous on a non-cloud one, and
           on cloud an auto-cleared alert (-1) is not a response at all. See
           docs/REPORT_PROC_AUDIT_PLAN.md section 3.

           Load-bearing, not cosmetic: the Lambda defaults this to falsey, so if it
           is ever dropped a cloud campus silently gets the non-cloud proc -- a
           wrong number with no error. */
        is_cloud: isCloud ? 1 : 0,
        username: username || '',
        campus_id: campusId,
        start_date: startDate,
        end_date: endDate,
        event_category_filter: '1',
        /* All 11 filter fields. device_filter was missing before B14 although the drawer
           offers Devices and getExecutiveReport passes it to the reason chart and the
           sp_get* section procs. */
        ...reportFilterFields(filters),
        /* [enabled, 'lt'|'gt'|'eq', minutes], as on the other reports. Not the Excess
           Alarms threshold — that is excessive_filter, below. */
        resp_filter_items: responseTimeField(filters),
        excessive_filter: execTime,
        items: {
            weekly_residents: 0,
            weekly_caregivers: 0,
            excessive_alerts: 1,
            daily_areas: 1,
            daily_area_types: 0,
            reason_data: 1,
            area_types_response: 1,
            area_types_response_chart: 1,
        },
    };

    const res = await postReport(body, '[executiveApi] fetchExecutiveReport error');
    if (res.ErrorCode) return { ErrorCode: res.ErrorCode };
    const data = res.data;

    return {
        excessiveAlerts: data?.excessive_alerts || [],
        reasonData: data?.reason_data || {},
        areaTypesResponse: data?.area_types_response || [],
        areaTypesResponseChart: data?.area_types_response_chart || {},
        dailyAreas: data?.daily_areas || [],
    };
}

// ---------------------------------------------------------------------------
// Fetch Filter Options (same as AlertDetails — full set)
// ---------------------------------------------------------------------------


// ---------------------------------------------------------------------------
// Save Excessive Response Threshold
// ---------------------------------------------------------------------------

/**
 * @param {number} campusId
 * @param {number} execTime — new threshold in minutes
 * @param {number} oldExecTime — previous threshold
 * @param {string} userId
 * @param {string} clientId
 * @returns {Promise<[boolean, string?]>}
 */
export async function saveExecThreshold(campusId, execTime, oldExecTime, userId, clientId) {
    return apiSave({
        table: 'core_campus',
        payload: [{ campus_id: campusId, exec_excess_time: execTime, modified_by: userId }],
        oldPayload: [{ campus_id: campusId, exec_excess_time: oldExecTime }],
        pKey: 'campus_id',
        condition: 'primary',
        method: 'POST',
        campusId,
        clientId,
        userId,
    });
}

/** The full filter option set (utils/reportFilterOptions.js). Executive now gets its event
    types sorted, like every other report. */
export { fetchFilterOptions as fetchExecutiveFilterOptions } from '../../../../utils/reportFilterOptions.js';
